import queryString from 'query-string';
import callApi, { CallApiResult, getCustomerId } from 'src/api';

export interface ChipTransactionQueryParams {
    from?: string;
    to?: string;
    machineId?: string;
    page?: number;
    size?: number;
}

export const fetchVendingOrgs = (): Promise<CallApiResult> => {
    return callApi('/api/vending/org');
};

export const createVendingOrg = (data: unknown): Promise<CallApiResult> => {
    return callApi('/api/vending/org', { method: 'POST', data });
};

export const fetchCustomerChips = (customerId: string = getCustomerId()): Promise<CallApiResult> => {
    return callApi(`/api/vending/customer/${customerId}/chips`);
};

export const fetchCustomerVendingMemberships = (customerId: string = getCustomerId()): Promise<CallApiResult> => {
    return callApi(`/api/vending/customer/${customerId}/memberships`);
};

/* Org members — machine-scoped viewer access management */
export const fetchOrgMembers = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/members`);
};

export const addOrgMember = (orgId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/member`, { method: 'POST', data });
};

export const updateOrgMember = (orgId: string, customerId: number, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/member/${customerId}`, { method: 'PATCH', data });
};

export const removeOrgMember = (orgId: string, customerId: number): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/member/${customerId}`, { method: 'DELETE' });
};

export interface OrgChipQueryParams {
    page?: number;
    size?: number;
    sort?: string;
    direction?: string;
    firstName?: string;
    lastName?: string;
    externalId?: string;
    companyNumber?: string;
    serial?: string;
    type?: string;
    status?: string;
    missingFromImport?: boolean;
}

export const fetchOrgChips = (orgId: string, params: OrgChipQueryParams = {}): Promise<CallApiResult> => {
    const query = queryString.stringify(params, { skipEmptyString: true, skipNull: true });
    return callApi(`/api/vending/org/${orgId}/chips${query !== '' ? '?' + query : ''}`);
};

export const fetchOrgOverview = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/overview`);
};

export const fetchOrgYearly = (orgId: string, year: number): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/yearly?year=${year}`);
};

export interface OrgTransactionQueryParams {
    search?: string;
    from?: string;
    to?: string;
    type?: string;
    machineId?: string;
    page?: number;
    size?: number;
}

const orgTransactionsQuery = (params: OrgTransactionQueryParams): string => {
    const query = queryString.stringify(params, { skipEmptyString: true, skipNull: true });
    return query !== '' ? '?' + query : '';
};

export const fetchOrgTransactions = (orgId: string, params: OrgTransactionQueryParams = {}): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/transactions${orgTransactionsQuery(params)}`);
};

export const fetchOrgMachines = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/machines`);
};

export const createOrgMachine = (orgId: string, data: { name: string }): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/machine`, { method: 'POST', data });
};

export const orgTransactionsCsvUrl = (orgId: string, params: OrgTransactionQueryParams = {}): string =>
    `/api/vending/org/${orgId}/transactions/csv${orgTransactionsQuery(params)}`;

export const importOrgTransactions = (orgId: string, formData: FormData): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/transactions/import`, { method: 'POST', data: formData });
};

/* Machine-level vends export: imports the cash sales (wallet-paid rows come through the wallet feed) */
export const importOrgVends = (orgId: string, formData: FormData): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/vends/import`, { method: 'POST', data: formData });
};

/* Cancel/uncancel an imported transaction (FrontOffice) — excluded from all exports/aggregations */

export const cancelOrgTransaction = (transactionId: string, reason?: string): Promise<CallApiResult> => {
    const query = queryString.stringify({ reason }, { skipEmptyString: true, skipNull: true });
    return callApi(`/api/vending/transactions/${transactionId}/cancel${query !== '' ? '?' + query : ''}`, {
        method: 'POST',
    });
};

export const uncancelOrgTransaction = (transactionId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/transactions/${transactionId}/uncancel`, { method: 'POST' });
};

export const importOrgChips = (orgId: string, formData: FormData): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/chips/import`, { method: 'POST', data: formData });
};

export const lookupChipByCardNumber = (orgId: string, cardNumber: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/chips/by-card-number/${encodeURIComponent(cardNumber)}`);
};

export const correctChip = (
    orgId: string,
    data: { serialNumber: string; cardNumber: string }
): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/chips/correct`, { method: 'POST', data });
};

export const televendExportCsvUrl = (orgId: string): string => `/api/vending/org/${orgId}/chips/televend-export/csv`;

export const registrationInvitesCsvUrl = (orgId: string): string =>
    `/api/vending/org/${orgId}/registration-invites/csv`;

// --- public employee self-registration (set-password page) — no customer context/auth ---

export interface ChipRegistrationPrefill {
    email: string;
    firstName: string;
    lastName: string;
}

export const resolveChipRegistration = (token: string): Promise<CallApiResult<ChipRegistrationPrefill>> => {
    return callApi<ChipRegistrationPrefill>('/api/vending/registration/resolve', { params: { token } });
};

export const completeChipRegistration = (data: {
    token: string;
    password: string;
    acceptedAgb: boolean;
    acceptedPrivacy: boolean;
}): Promise<CallApiResult> => {
    return callApi('/api/vending/registration/complete', { method: 'POST', data });
};

export const monthlyCsvUrl = (orgId: string, yearMonth: string): string =>
    `/api/vending/org/${orgId}/monthly/${yearMonth}/csv`;

export const monthlyPdfUrl = (orgId: string, yearMonth: string): string =>
    `/api/vending/org/${orgId}/monthly/${yearMonth}/pdf`;

export const lohnverrechnungCsvUrl = (orgId: string, yearMonth: string): string =>
    `/api/vending/org/${orgId}/monthly/${yearMonth}/lohnverrechnung/csv`;

/* Billing Lohnverrechnung export: exports ALL non-billed salary transactions (may span months)
   and marks them billed; the run id (X-Lv-Run-Id response header) reverses the run. */

export const lohnverrechnungExportAllUrl = (orgId: string): string =>
    `/api/vending/org/${orgId}/lohnverrechnung/export`;

export interface LohnverrechnungRun {
    runId: string;
    billedAt: string;
    transactionCount: number;
    totalAmount: number;
}

export const fetchLohnverrechnungRuns = (orgId: string): Promise<CallApiResult<LohnverrechnungRun[]>> => {
    return callApi<LohnverrechnungRun[]>(`/api/vending/org/${orgId}/lohnverrechnung/runs`);
};

export const unbillLohnverrechnungRun = (orgId: string, runId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/lohnverrechnung/unbill/${runId}`, { method: 'POST' });
};

export const fetchChip = (chipId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}`);
};

export const fetchChipTransactions = (
    chipId: string,
    params: ChipTransactionQueryParams = {}
): Promise<CallApiResult> => {
    let query = queryString.stringify(params, { skipEmptyString: true, skipNull: true });
    if (query !== '') query = '?' + query;

    return callApi(`/api/vending/chip/${chipId}/transactions${query}`);
};

export const chipTransactionsCsvUrl = (chipId: string, params: ChipTransactionQueryParams = {}): string => {
    let query = queryString.stringify(params, { skipEmptyString: true, skipNull: true });
    if (query !== '') query = '?' + query;
    return `/api/vending/chip/${chipId}/transactions/csv${query}`;
};

export const fetchChipMachines = (chipId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/machines`);
};

export const fetchChipYearly = (chipId: string, year: number): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/yearly?year=${year}`);
};

export const chipMonthlyCsvUrl = (chipId: string, yearMonth: string): string =>
    `/api/vending/chip/${chipId}/monthly/${yearMonth}/csv`;

export const chipMonthlyPdfUrl = (chipId: string, yearMonth: string): string =>
    `/api/vending/chip/${chipId}/monthly/${yearMonth}/pdf`;

export const setChipReminder = (chipId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/reminder`, { method: 'PATCH', data });
};

// ---- caller-scoped ("my chips"): chipId optional, omit to span all of the caller's chips ----

export interface MyTransactionQueryParams {
    chipId?: string;
    from?: string;
    to?: string;
    machineId?: string;
    page?: number;
    size?: number;
}

const myQuery = (params: queryString.StringifiableRecord): string => {
    const query = queryString.stringify(params, { skipEmptyString: true, skipNull: true });
    return query !== '' ? '?' + query : '';
};

export const fetchMyTransactions = (params: MyTransactionQueryParams = {}): Promise<CallApiResult> => {
    return callApi(`/api/vending/my/transactions${myQuery(params)}`);
};

export const myTransactionsCsvUrl = (params: MyTransactionQueryParams = {}): string =>
    `/api/vending/my/transactions/csv${myQuery(params)}`;

export const fetchMyMachines = (): Promise<CallApiResult> => {
    return callApi('/api/vending/my/machines');
};

export const fetchMyYearly = (year: number, chipId?: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/my/yearly${myQuery({ year, chipId })}`);
};

export const myMonthlyCsvUrl = (yearMonth: string, chipId?: string): string =>
    `/api/vending/my/monthly/${yearMonth}/csv${myQuery({ chipId })}`;

export const myMonthlyPdfUrl = (yearMonth: string, chipId?: string): string =>
    `/api/vending/my/monthly/${yearMonth}/pdf${myQuery({ chipId })}`;

export const fetchChipHistory = (chipId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/history`);
};

export const resendChipRegistration = (chipId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/resend-registration`, { method: 'POST' });
};

export const requestChipTypeChange = (chipId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/request-type-change`, { method: 'POST' });
};

export const createChip = (orgId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/chip`, { method: 'POST', data });
};

export const linkChip = (chipId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/link`, { method: 'POST', data });
};

export const assignChip = (chipId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/assign`, { method: 'POST', data });
};

export const fetchOrgPersons = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/persons`);
};

export const updateChip = (chipId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}`, { method: 'PATCH', data });
};

export const topUpChip = (chipId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/chip/${chipId}/topup`, { method: 'POST', data });
};

/* Settlement (Abrechnung) — org fees, external owners, machine ownership, product billing */

export const fetchSettlementConfig = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/settlement-config`);
};

export const updateSettlementConfig = (orgId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/settlement-config`, { method: 'PUT', data });
};

export const fetchSettlementParties = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/settlement-parties`);
};

export const createSettlementParty = (orgId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/settlement-party`, { method: 'POST', data });
};

export const updateSettlementParty = (orgId: string, partyId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/settlement-party/${partyId}`, { method: 'PATCH', data });
};

export const deleteSettlementParty = (orgId: string, partyId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/settlement-party/${partyId}`, { method: 'DELETE' });
};

export const updateMachineSettlement = (orgId: string, machineId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/machine/${machineId}/settlement`, { method: 'PATCH', data });
};

export const fetchProductBilling = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/product-billing`);
};

export const updateProductBilling = (orgId: string, data: unknown): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/product-billing`, { method: 'PUT', data });
};

export const fetchUnmappedProducts = (orgId: string): Promise<CallApiResult> => {
    return callApi(`/api/vending/org/${orgId}/product-billing/unmapped`);
};

export const externalReportCsvUrl = (orgId: string, yearMonth: string, partyId: string): string =>
    `/api/vending/org/${orgId}/settlement/${yearMonth}/external-report/${partyId}/csv`;
